import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Send, Phone, Mail, MessageCircle, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { contactApi } from '@/lib/api';
import { siteConfig as staticConfig } from '@/lib/config';
import { useSiteConfig } from '@/hooks/use-site-settings';
import ProceduralGroundBackground from '@/components/ui/procedural-ground-background';

const Contato = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { data: dynamicConfig } = useSiteConfig();

  const config = {
    email: dynamicConfig?.contact_email || staticConfig.email,
    phone: dynamicConfig?.contact_phone || staticConfig.phone,
    whatsapp: dynamicConfig?.contact_phone?.replace(/\D/g, '') || staticConfig.whatsapp,
    benefits: staticConfig.benefits
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    budget: '',
    message: '',
    timeline: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await contactApi.create({
        name: formData.name,
        email: formData.email,
        company: formData.company || undefined,
        phone: formData.phone || undefined,
        service: formData.service || undefined,
        budget: formData.budget || undefined,
        timeline: formData.timeline || undefined,
        message: formData.message
      });

      toast({
        title: "Mensagem enviada!",
        description: "Entraremos em contato em até 24h. Obrigado pelo interesse!",
      });

      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        budget: '',
        message: '',
        timeline: ''
      });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast({
        title: "Erro ao enviar",
        description: "Ocorreu um erro ao enviar sua mensagem. Tente novamente ou entre em contato pelo WhatsApp.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getWhatsApp = (message?: string) => {
    const baseUrl = `https://wa.me/${config.whatsapp}`;
    return message ? `${baseUrl}?text=${encodeURIComponent(message)}` : baseUrl;
  }

  const contactMethods = [
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Resposta rápida',
      href: getWhatsApp('Olá! Gostaria de conversar sobre um projeto.'),
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Mail,
      title: 'Email',
      description: config.email,
      href: `mailto:${config.email}`,
      color: 'from-blue-500 to-indigo-500'
    },
    {
      icon: Phone,
      title: 'Telefone',
      description: config.phone,
      href: `tel:${config.phone.replace(/\D/g, '')}`,
      color: 'from-indigo-500 to-blue-600'
    }
  ];

  const benefits = config.benefits;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white relative overflow-hidden">
      {/* Procedural Background */}
      <ProceduralGroundBackground className="fixed inset-0 w-full h-full z-0 opacity-30" />

      {/* Gradient Overlays */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a] z-0 pointer-events-none" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent z-0 pointer-events-none" />

      <div className="relative z-10">
        {/* Hero Header */}
        <section className="pt-28 pb-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-3xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-blue-500/10 border border-blue-500/20"
              >
                <Sparkles className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-400">Vamos criar algo incrível</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Pronto para{' '}
                <span className="gradient-text-primary">
                  transformar
                </span>
                {' '}sua marca?
              </h1>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Conte-me sobre seu projeto e vamos descobrir juntos como criar uma experiência visual que impacta.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Quick Contact Methods */}
        <section className="pb-16">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {contactMethods.map((method, index) => (
                <motion.a
                  key={method.title}
                  href={method.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="group relative p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <method.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{method.title}</h3>
                  <p className="text-sm text-gray-400">{method.description}</p>
                  <ArrowRight className="absolute top-6 right-6 w-5 h-5 text-gray-600 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="pb-24">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-6xl mx-auto">

              {/* Form Section */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="lg:col-span-3"
              >
                <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-white mb-2">Briefing do Projeto</h2>
                    <p className="text-gray-400">
                      Quanto mais detalhes você compartilhar, melhor será minha proposta.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-300">Nome completo *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Seu nome"
                          required
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500/50"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-300">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="seu@email.com"
                          required
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500/50"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-gray-300">Empresa</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => handleInputChange('company', e.target.value)}
                          placeholder="Nome da empresa"
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500/50"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-gray-300">WhatsApp</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="(11) 99999-9999"
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500/50"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-gray-300">Tipo de serviço</Label>
                        <Select value={formData.service} onValueChange={(value) => handleInputChange('service', value)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue placeholder="Selecione um serviço" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1a] border-white/10">
                            <SelectItem value="branding">Branding & Identidade Visual</SelectItem>
                            <SelectItem value="uiux">UI/UX Design</SelectItem>
                            <SelectItem value="campanhas">Direção de Arte & Campanhas</SelectItem>
                            <SelectItem value="editorial">Design Editorial</SelectItem>
                            <SelectItem value="ilustracao">Ilustração & Arte Digital</SelectItem>
                            <SelectItem value="motion">Motion Graphics & Vídeo</SelectItem>
                            <SelectItem value="ia">IA & Personagens Digitais</SelectItem>
                            <SelectItem value="fullstack">Desenvolvimento Fullstack</SelectItem>
                            <SelectItem value="3d">Modelagem 3D</SelectItem>
                            <SelectItem value="outro">Outro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="budget" className="text-gray-300">Orçamento estimado</Label>
                        <Select value={formData.budget} onValueChange={(value) => handleInputChange('budget', value)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue placeholder="Faixa de investimento" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1a] border-white/10">
                            <SelectItem value="5k">Até R$ 5.000</SelectItem>
                            <SelectItem value="10k">R$ 5.000 - R$ 10.000</SelectItem>
                            <SelectItem value="25k">R$ 10.000 - R$ 25.000</SelectItem>
                            <SelectItem value="50k">R$ 25.000 - R$ 50.000</SelectItem>
                            <SelectItem value="50k+">Acima de R$ 50.000</SelectItem>
                            <SelectItem value="conversar">Prefiro conversar</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-gray-300">Conte-me sobre seu projeto *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        placeholder="Descreva seu projeto, objetivos, público-alvo, inspirações..."
                        className="min-h-32 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500/50"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isLoading}
                      className="w-full liquid-glass-btn-solid text-white rounded-xl h-14 text-lg font-medium"
                    >
                      {isLoading ? (
                        <>
                          <Clock className="mr-2 h-5 w-5 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Enviar briefing
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </motion.div>

              {/* Sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="lg:col-span-2 space-y-6"
              >
                {/* Benefits */}
                <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
                  <h3 className="text-xl font-bold text-white mb-6">O que você ganha</h3>
                  <div className="space-y-4">
                    {benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                      >
                        <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="h-4 w-4 text-blue-400" />
                        </div>
                        <span className="text-gray-300 text-sm">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Schedule */}
                <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-indigo-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Disponibilidade</h3>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Segunda a Sexta</span>
                      <span className="text-white text-sm font-medium">09h - 18h</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Sábado</span>
                      <span className="text-white text-sm font-medium">10h - 14h</span>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-white/10">
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <MapPin className="w-4 h-4" />
                      <span>Rio de Janeiro, Brasil</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Atendo clientes de todo o Brasil remotamente
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contato;